import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Nav } from "../components/Nav";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back");
      nav(user.role === "admin" ? "/admin" : next);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Login failed"));
    } finally { setLoading(false); }
  };
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-lg mx-auto px-6 md:px-10 pt-24 pb-32">
        <p className="overline mb-8"><span className="gold-line mr-3" />Sign in</p>
        <h1 className="font-serif-display font-light text-5xl leading-[1.05]">Welcome back.</h1>
        <form onSubmit={submit} className="mt-16 space-y-6" data-testid="login-form">
          <div>
            <label className="overline">Email</label>
            <input required type="email" data-testid="login-email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          </div>
          <div>
            <label className="overline">Password</label>
            <input required type="password" data-testid="login-password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          </div>
          <button disabled={loading} data-testid="login-submit" className="btn-editorial w-full">{loading ? "Signing in…" : "Sign in"}</button>
          <div className="flex items-center justify-between pt-4">
            <Link data-testid="login-signup-link" to={next && next !== "/dashboard" ? `/signup?next=${encodeURIComponent(next)}` : "/signup"} className="overline link-underline">New here? Sign up</Link>
            <Link data-testid="login-forgot-link" to="/forgot-password" className="overline link-underline">Forgot?</Link>
          </div>
        </form>
      </section>
    </div>
  );
}

export function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form.name, form.email, form.password);
      toast.success("Welcome to the studio");
      nav(next);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Signup failed"));
    } finally { setLoading(false); }
  };
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-lg mx-auto px-6 md:px-10 pt-24 pb-32">
        <p className="overline mb-8"><span className="gold-line mr-3" />Create Account</p>
        <h1 className="font-serif-display font-light text-5xl leading-[1.05]">Begin your journey.</h1>
        <form onSubmit={submit} className="mt-16 space-y-6" data-testid="signup-form">
          <div>
            <label className="overline">Name</label>
            <input required data-testid="signup-name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          </div>
          <div>
            <label className="overline">Email</label>
            <input required type="email" data-testid="signup-email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          </div>
          <div>
            <label className="overline">Password</label>
            <input required type="password" data-testid="signup-password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          </div>
          <button disabled={loading} data-testid="signup-submit" className="btn-editorial w-full">{loading ? "Creating…" : "Create Account"}</button>
          <Link to={next && next !== "/dashboard" ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="overline link-underline block pt-2" data-testid="signup-login-link">Already have an account? Sign in</Link>
        </form>
      </section>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email });
      toast.success("Check your email for the reset token");
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-lg mx-auto px-6 md:px-10 pt-24 pb-32">
        <p className="overline mb-8"><span className="gold-line mr-3" />Password Reset</p>
        <h1 className="font-serif-display font-light text-4xl">Forgot your password?</h1>
        <form onSubmit={submit} className="mt-12 space-y-6" data-testid="forgot-form">
          <input required type="email" data-testid="forgot-email" placeholder="Your email" value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          <button disabled={loading} data-testid="forgot-submit" className="btn-editorial w-full">{loading ? "Sending…" : "Send Reset Link"}</button>
          <Link to="/reset-password" className="overline link-underline block">Have a token? Reset here</Link>
        </form>
      </section>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ token: params.get("token") || "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/password-reset", form);
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) { toast.error(apiErrorMessage(err, "Failed")); }
    finally { setLoading(false); }
  };
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <section className="max-w-lg mx-auto px-6 md:px-10 pt-24 pb-32">
        <p className="overline mb-8"><span className="gold-line mr-3" />New Password</p>
        <h1 className="font-serif-display font-light text-4xl">Set a new password.</h1>
        <form onSubmit={submit} className="mt-12 space-y-6" data-testid="reset-form">
          <input required data-testid="reset-token" placeholder="Reset token" value={form.token} onChange={(e)=>setForm({...form, token: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          <input required type="password" data-testid="reset-password" placeholder="New password" value={form.new_password} onChange={(e)=>setForm({...form, new_password: e.target.value})} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
          <button disabled={loading} data-testid="reset-submit" className="btn-editorial w-full">{loading ? "Saving…" : "Reset Password"}</button>
        </form>
      </section>
    </div>
  );
}
